/**
 * tools/shared.tsx — small presentational pieces shared by the three Stage 3.5 Dataverse pages.
 *
 * Deliberately RAW (Stage 3.5 brief: "do not make the UI pretty"). One inline <style> block per
 * concern, class names local to these pages, reusing the frame's CSS tokens (App.tsx :root). No new
 * dependency, no design polish — just legible, labelled, accessible controls.
 *
 * The pieces here are the states every Dataverse page shares: a signed-out prompt (identical to the
 * one on /me) and a "feature not available in this build" note (the FEATURE_DATAVERSE_ARTEFACT-off
 * degradation). Keeping them here stops the three pages drifting on the wording of a shared state.
 */

/** Raw shared styles for the Dataverse pages. Plain tables/forms over the frame's tokens. */
export const TOOLS_STYLES = `
  .tb-tools { max-width: 60rem; }
  .tb-tools h1 { margin-top: 0; }
  .tb-tools table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
  .tb-tools th, .tb-tools td {
    border: 1px solid var(--tb-line);
    padding: 0.4rem 0.6rem;
    text-align: left;
    vertical-align: top;
    font-size: 0.9rem;
  }
  .tb-tools th { background: var(--tb-surface); }
  .tb-tools dl {
    display: grid;
    grid-template-columns: minmax(9rem, max-content) 1fr;
    gap: 0.4rem 1.25rem;
    margin: 1rem 0;
  }
  .tb-tools dt { color: var(--tb-muted); font-weight: 600; }
  .tb-tools dd { margin: 0; }
  .tb-tools .tb-muted { color: var(--tb-muted); }
  /*
    DS-F07 RESULT CARDS. The catalogue's results were an 11-column table, which cannot show a trust
    signal set: a signal that is absent for most tools (health when Healthy, open issues when none)
    either becomes a mostly-empty column or forces a value into every row that §8 says to hide.
    A card shows only the signals that have something to say about THAT tool.

    Still raw, per the Stage 3.5 brief. No backticks in this comment: TOOLS_STYLES is a template
    literal, so one would end the string. auto-fill with a minimum means the grid is one column on a
    phone and as many as fit on a desktop with no media query, and align-content:start keeps a short
    last row from stretching.
  */
  .tb-card-grid {
    list-style: none;
    margin: 1rem 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
    align-content: start;
    gap: 0.75rem;
  }
  .tb-card {
    border: 1px solid var(--tb-line);
    border-radius: 8px;
    background: var(--tb-surface);
    padding: 0.7rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .tb-card__head { display: flex; gap: 0.4rem; align-items: baseline; flex-wrap: wrap; }
  .tb-tools .tb-card__title { margin: 0; font-size: 1rem; }
  /* Signal 2's icon. A pill rather than a bare glyph so "!" reads as a marker, not as punctuation. */
  .tb-card__marker {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--tb-muted);
    border: 1px solid var(--tb-line);
    border-radius: 999px;
    padding: 0 0.4rem;
    line-height: 1.4;
  }
  .tb-card__desc { margin: 0; font-size: 0.9rem; }
  .tb-card__by { margin: 0; font-size: 0.85rem; color: var(--tb-muted); }
  /* The trust signals. Same <dl> and same labels as the tool page's Trust list, tighter (DS-NFR06). */
  .tb-tools .tb-card__signals {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.1rem 0.6rem;
    margin: 0.1rem 0 0;
    font-size: 0.85rem;
  }
  .tb-card__meta { margin: 0; font-size: 0.8rem; color: var(--tb-muted); }
  .tb-card__meta > span + span::before { content: ' · '; }
  /* "Description of connectors" disclosure — read-only per-step setup descriptions on the tool page. */
  .tb-connector-desc {
    border: 1px solid var(--tb-line);
    border-radius: 8px;
    padding: 0.5rem 0.85rem;
    margin: 1rem 0;
    background: var(--tb-surface);
  }
  .tb-connector-desc > summary {
    cursor: pointer;
    font-size: 0.95rem;
    padding: 0.15rem 0;
  }
  .tb-connector-desc > summary strong { font-size: 1rem; }
  .tb-connector-steps { list-style: none; margin: 0.75rem 0 0; padding: 0; }
  .tb-connector-step {
    border-left: 3px solid var(--tb-line);
    padding: 0.4rem 0 0.4rem 0.75rem;
    margin: 0.6rem 0;
  }
  .tb-connector-step__head { display: flex; gap: 0.5rem; align-items: baseline; flex-wrap: wrap; }
  .tb-connector-step__num { font-weight: 700; color: var(--tb-muted); font-variant-numeric: tabular-nums; }
  .tb-connector-step__branch {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tb-muted);
    border: 1px solid var(--tb-line);
    border-radius: 999px;
    padding: 0.02rem 0.45rem;
  }
  .tb-connector-step__label { font-weight: 600; }
  .tb-connector-step__meta { color: var(--tb-muted); font-size: 0.8rem; margin-top: 0.15rem; }
  .tb-connector-step__body { margin: 0.35rem 0 0; white-space: pre-wrap; }
  .tb-tools .tb-mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85rem;
    word-break: break-all;
  }
  .tb-tools form { max-width: 40rem; }
  .tb-tools fieldset { border: 1px solid var(--tb-line); border-radius: 6px; margin: 1rem 0; padding: 0.75rem 1rem; }
  .tb-tools legend { font-weight: 600; padding: 0 0.4rem; }
  .tb-tools label.tb-field { display: block; margin: 0.6rem 0; }
  .tb-tools label.tb-field > span { display: block; font-weight: 600; margin-bottom: 0.2rem; }
  /* V1's bare-element rules. These style every button/input/textarea/select under
     .tb-tools and are part of V1's clothing.

     They used to carry ":not(.tb-ov-reskin *)" exemptions, because the V2 upload
     overlay mounted INSIDE this section and a two-class selector (0,1,1) beats the
     single-class Tailwind utilities the design's components use (bg-transparent,
     border-control, text-text) — so every design Button came out with V1's accent
     fill and every FormField box got V1's border, padding and 4rem min-height.

     THE EXEMPTIONS ARE GONE, AND THE COLLISION IS NOT — read this before touching
     the rules below. Two facts, both verified in the built output rather than
     assumed:

       1. The V2 overlay IS still a descendant of this section.tb-tools (Share.tsx
          renders FlowPicker inside it). Fixed positioning moves an element visually
          but NOT out of the DOM subtree, so ".tb-tools button" (0,1,1) still MATCHES
          every design Button and out-ranks the single-class utilities they use
          (bg-transparent, border-control, text-text).
       2. Cascade layers do NOT arbitrate this. Tailwind's at-layer is a build-time
          directive that PostCSS flattens; the built CSS contains zero real at-layer
          rules. Nor does source order: TOOLS_STYLES is injected from a runtime
          style element, so it is not in that stylesheet at all.

     So V1's rules win wherever they match a design component. The exemptions were
     dropped because V1 is now a FEATURE-INVENTORY reference — read for which
     sections exist, not for appearance — and the two trees are meant to stop being
     nested, which is the actual fix. If you see a design Button wearing V1's accent
     fill, that is this, and the fix is to lift the overlay out of .tb-tools, NOT to
     re-add a :not() hack — that hack is what made this file unreadable before.

     (No backticks or dollar-brace in this comment: it lives inside a JS template
     literal, so either one would terminate or interpolate the string.) */
  .tb-tools input[type="text"],
  .tb-tools input[type="number"],
  .tb-tools textarea,
  .tb-tools select {
    width: 100%;
    font: inherit;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--tb-line);
    border-radius: 6px;
    background: var(--tb-bg);
    color: var(--tb-ink);
  }
  .tb-tools textarea { min-height: 4rem; resize: vertical; }
  .tb-tools button {
    font: inherit;
    padding: 0.5rem 1rem;
    border: 1px solid var(--tb-accent);
    border-radius: 6px;
    background: var(--tb-accent);
    color: var(--tb-accent-ink);
    cursor: pointer;
  }
  .tb-tools button:disabled { opacity: 0.5; cursor: not-allowed; }
  /* Free-text tag chips (Share form). Raw, token-based, no polish — a list of pills over the
     Stage 3.5 form. The remove control is a small text button, NOT the big accent submit button. */
  .tb-tools ul.tb-chips { list-style: none; display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 0 0 0.4rem; padding: 0; }
  .tb-tools ul.tb-chips li {
    display: inline-flex; align-items: center; gap: 0.35rem;
    border: 1px solid var(--tb-line); border-radius: 999px;
    padding: 0.15rem 0.2rem 0.15rem 0.6rem;
    background: var(--tb-surface); font-size: 0.85rem;
  }
  .tb-tools button.tb-chip-remove {
    font: inherit; line-height: 1; padding: 0 0.35rem;
    border: 0; border-radius: 999px; background: transparent; color: var(--tb-muted); cursor: pointer;
  }
  .tb-tools button.tb-chip-remove:hover { color: var(--tb-ink); }
  .tb-tools .tb-field-hint { display: block; font-weight: 400; color: var(--tb-muted); font-size: 0.85rem; margin-top: 0.2rem; }
  .tb-tools .tb-note {
    border: 1px solid var(--tb-line);
    border-left: 4px solid var(--tb-accent);
    background: var(--tb-surface);
    padding: 0.6rem 0.9rem;
    margin: 1rem 0;
    border-radius: 4px;
  }
  .tb-tools .tb-note--warn { border-left-color: var(--tb-banner-line); background: var(--tb-banner-bg); color: var(--tb-banner-ink); }
  .tb-tools .tb-note--ok { border-left-color: #2e7d32; }
  .tb-tools a { color: var(--tb-accent); }
  /* Matches Profile.tsx's PROFILE_STYLES .tb-private__marker — same private-content marker (FB-F02/F03),
     reused here so Save gets the same visual language without importing a whole second stylesheet. */
  .tb-tools .tb-private__marker {
    display: inline-block; font-size: 0.75rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-accent-ink); background: var(--tb-accent);
    border-radius: 4px; padding: 0.1rem 0.4rem;
  }
  .tb-signin-btn {
    display: inline-block;
    margin-top: 1rem;
    padding: 0.55rem 1rem;
    background: var(--tb-accent);
    color: var(--tb-accent-ink);
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
  }
`;

/** The signed-out prompt — same shape and wording family as Profile.tsx's SignedOut. */
export function SignedOut({ title, returnTo }: { title: string; returnTo: string }) {
  return (
    <section aria-labelledby="signin-heading" className="tb-tools">
      <style>{TOOLS_STYLES}</style>
      <h1 id="signin-heading">{title}</h1>
      <p style={{ maxWidth: '52ch' }}>
        Sign in with your work account to continue. Trailblazer talks to Microsoft only through our
        own backend — this page never sees your password or any token.
      </p>
      {/* Plain link (full-page navigation) to the BACKEND login route; the backend drives the
          Microsoft round-trip and returns here afterwards. */}
      <a className="tb-signin-btn" href={`/auth/login?returnTo=${encodeURIComponent(returnTo)}`}>
        Sign in with your work account
      </a>
    </section>
  );
}

/**
 * The FEATURE_DATAVERSE_ARTEFACT-off state: the route family is not mounted, so its calls 404. We
 * say plainly that the capability is not in this build — no "coming soon", no named future feature
 * (PC-F14); the nav entries themselves already exist by requirement.
 */
export function FeatureDisabled({ title }: { title: string }) {
  return (
    <section aria-labelledby="disabled-heading" className="tb-tools">
      <style>{TOOLS_STYLES}</style>
      <h1 id="disabled-heading">{title}</h1>
      <p style={{ maxWidth: '52ch' }}>Tool sharing and adoption are not available in this build.</p>
    </section>
  );
}
